/**
 * Company Detail Page — Shows branches for a company with settings tab.
 * Settings here are the root defaults inherited by all branches and weeks.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as api from '../services/routeOptimizerService';
import SettingsPanel from '../components/SettingsPanel';
import '../styles/RouteOptimizerApp.css';

export default function CompanyDetail() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const [companyName, setCompanyName] = useState('');
  const [loading, setLoading] = useState(true);
  const [newBranchName, setNewBranchName] = useState('');
  const [creatingBranch, setCreatingBranch] = useState(false);
  const [activeTab, setActiveTab] = useState('branches');
  const [companySettings, setCompanySettings] = useState(null);

  const loadBranches = useCallback(async () => {
    try {
      const data = await api.listBranches(companyId);
      setBranches(data);
      // Try to get company name from companies list
      const companies = await api.listCompanies();
      const company = companies.find(c => c.id === companyId);
      if (company) setCompanyName(company.name);
    } catch (err) {
      console.error('Failed to load branches:', err);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { loadBranches(); }, [loadBranches]);

  const loadCompanySettings = useCallback(async () => {
    try {
      const s = await api.getCompanySettings(companyId);
      setCompanySettings(s);
    } catch (err) {
      console.error('Failed to load company settings:', err);
    }
  }, [companyId]);

  useEffect(() => { if (activeTab === 'settings') loadCompanySettings(); }, [activeTab, loadCompanySettings]);

  const handleCreateBranch = async (e) => {
    e.preventDefault();
    if (!newBranchName.trim()) return;
    setCreatingBranch(true);
    try {
      await api.createBranch(companyId, { name: newBranchName.trim() });
      setNewBranchName('');
      await loadBranches();
    } catch (err) {
      console.error('Failed to create branch:', err);
    } finally {
      setCreatingBranch(false);
    }
  };

  const handleDeleteBranch = async (bid) => {
    if (!window.confirm('Delete this branch and all its data?')) return;
    await api.deleteBranch(bid);
    await loadBranches();
  };

  if (loading) return <div className="ro-loading">Loading...</div>;

  return (
    <div className="ro-dashboard">
      {/* Breadcrumb */}
      <div className="ro-breadcrumb">
        <a href="/routeoptimizer">Dashboard</a>
        <span className="ro-breadcrumb-sep">{'\u203A'}</span>
        <span className="ro-breadcrumb-current">{companyName || 'Company'}</span>
      </div>

      <div className="ro-header">
        <h1>{companyName || 'Company'}</h1>
        <p className="ro-subtitle">{branches.length} branch{branches.length !== 1 ? 'es' : ''}</p>
      </div>

      <div className="ro-tab-bar">
        <button
          className={`ro-tab ${activeTab === 'branches' ? 'active' : ''}`}
          onClick={() => setActiveTab('branches')}
        >
          Branches
        </button>
        <button
          className={`ro-tab ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          Settings
        </button>
      </div>

      {activeTab === 'settings' && (
        <div className="ro-card">
          <p className="ro-settings-info">
            These settings are the root defaults for this company.
            All new branches and weeks will inherit these settings.
          </p>
          {companySettings ? (
            <SettingsPanel
              entityId={companyId}
              entityType="company"
              settings={companySettings}
              onRefresh={loadCompanySettings}
            />
          ) : (
            <div className="ro-loading">Loading settings...</div>
          )}
        </div>
      )}

      {activeTab === 'branches' && (
        <div className="ro-card">
          {branches.length === 0 ? (
            <p className="ro-empty">No branches yet. Create one below.</p>
          ) : (
            <div className="ro-week-list">
              {branches.map(b => (
                <div
                  key={b.id}
                  className="ro-week-item"
                  onClick={() => navigate(`/routeoptimizer/branch/${b.id}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/routeoptimizer/branch/${b.id}`); } }}
                >
                  <div className="ro-week-info">
                    <h3>{b.name}</h3>
                    {b.location && <span className="ro-meta">{b.location}</span>}
                  </div>
                  <div className="ro-week-flags">
                    <span className="ro-tag">{b.week_count || 0} week{(b.week_count || 0) !== 1 ? 's' : ''}</span>
                  </div>
                  <button
                    type="button"
                    className="ro-btn ro-btn-danger ro-btn-sm"
                    onClick={(e) => { e.stopPropagation(); handleDeleteBranch(b.id); }}
                    aria-label={`Delete ${b.name}`}
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          )}

          <form onSubmit={handleCreateBranch} className="ro-create-form">
            <input
              type="text"
              value={newBranchName}
              onChange={e => setNewBranchName(e.target.value)}
              placeholder="New branch name"
              className="ro-input"
              disabled={creatingBranch}
            />
            <button type="submit" className="ro-btn ro-btn-primary" disabled={creatingBranch}>
              {creatingBranch ? 'Creating...' : 'Create Branch'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
